import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory, faDownload, faSearch, faSpinner, faChevronLeft, faChevronRight,
  faUser, faBox, faFolder, faShieldAlt, faCog, faPlus, faPen, faTrash,
  faUndo, faStar, faSignInAlt, faSignOutAlt, faKey, faTimes, faEye,
  faImage, faComment, faRobot, faExclamationTriangle, faCheckCircle,
  faUsers, faUserPlus, faEnvelope, faLayerGroup, faFileExport, faFileImport,
  faCreditCard, faBell, faCloudUploadAlt, faThumbtack, faExchangeAlt,
  faCrown, faGlobe, faCopy, faSort, faSortUp, faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { auditAPI, api } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import '../styles/AuditTrail.css';

/* ── Interfaces ──────────────────────────────────────────── */
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string | null;
  userName: string | null;
  userRole: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  resourceName: string | null;
  fieldChanged: string | null;
  oldValue: string | null;
  newValue: string | null;
  changeReason: string | null;
  source: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  device: string | null;
  metadata: any;
}

interface AuditStats {
  totalLogs: number;
  logsToday: number;
  uniqueUsers: number;
  topActions: { action: string; count: number }[];
}

/* ── Constants ──────────────────────────────────────────── */
const PER_PAGE_OPTIONS = [20, 50, 100];

/* ── Helpers ─────────────────────────────────────────────── */
const getActionIcon = (a: string) => {
  if (a.includes('CREATE') || a.includes('REGISTER')) return faPlus;
  if (a.includes('UPDATE') || a.includes('CHANGE')) return faPen;
  if (a.includes('DELETE') || a.includes('REMOVE')) return faTrash;
  if (a.includes('RESTORE') || a.includes('REVERT')) return faUndo;
  if (a.includes('FAVORITE')) return faStar;
  if (a.includes('LOGIN')) return faSignInAlt;
  if (a.includes('LOGOUT')) return faSignOutAlt;
  if (a.includes('PASSWORD') || a.includes('2FA')) return faKey;
  if (a.includes('ROLE') || a.includes('PERMISSION')) return faShieldAlt;
  if (a.includes('IMAGE') || a.includes('UPLOAD')) return faImage;
  if (a.includes('COMMENT_PIN')) return faThumbtack;
  if (a.includes('COMMENT')) return faComment;
  if (a.includes('AI_')) return faRobot;
  if (a.includes('INVITATION') || a.includes('INVITE')) return faEnvelope;
  if (a.includes('TEAM_MEMBER')) return faUserPlus;
  if (a.includes('TEAM')) return faUsers;
  if (a.includes('WORKSPACE_JOIN') || a.includes('WORKSPACE_LEAVE')) return faExchangeAlt;
  if (a.includes('WORKSPACE')) return faGlobe;
  if (a.includes('COLLECTION')) return faLayerGroup;
  if (a.includes('EXPORT')) return faFileExport;
  if (a.includes('IMPORT')) return faFileImport;
  if (a.includes('PLAN') || a.includes('BILLING') || a.includes('PAYMENT')) return faCreditCard;
  if (a.includes('STORAGE')) return faCloudUploadAlt;
  if (a.includes('NOTIFICATION')) return faBell;
  if (a.includes('TRANSFER_OWNERSHIP')) return faCrown;
  if (a.includes('FAILED') || a.includes('UNAUTHORIZED')) return faExclamationTriangle;
  return faHistory;
};

type EventClass = 'login' | 'logout' | '2fa' | 'signup' | 'create' | 'update' | 'delete' | 'view' | 'revert' | 'failed' | 'export' | 'settings';

const getEventClass = (a: string): EventClass => {
  if (a.includes('LOGIN') && a.includes('FAILED')) return 'failed';
  if (a.includes('LOGIN')) return 'login';
  if (a.includes('LOGOUT')) return 'logout';
  if (a.includes('2FA')) return '2fa';
  if (a.includes('REGISTER') || a.includes('SIGNUP')) return 'signup';
  if (a.includes('CREATE')) return 'create';
  if (a.includes('UPDATE') || a.includes('CHANGE')) return 'update';
  if (a.includes('DELETE') || a.includes('REMOVE')) return 'delete';
  if (a.includes('VIEW')) return 'view';
  if (a.includes('REVERT') || a.includes('RESTORE')) return 'revert';
  if (a.includes('FAILED') || a.includes('UNAUTHORIZED') || a.includes('DENIED')) return 'failed';
  if (a.includes('EXPORT') || a.includes('IMPORT')) return 'export';
  if (a.includes('SETTING') || a.includes('CONFIG')) return 'settings';
  return 'view';
};

const getStatusClass = (log: AuditLog): 'success' | 'failed' | 'pending' => {
  if (log.action.includes('FAILED') || log.action.includes('UNAUTHORIZED') || log.action.includes('DENIED')) return 'failed';
  return 'success';
};

const formatAction = (a: string) =>
  a.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

const fmtDate = (d: string) => {
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
    dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const ago = (d: string) => {
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const getUserInitials = (name: string | null) => {
  if (!name) return 'SY';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const avatarColors: Record<string, { bg: string; color: string }> = {};
const palette = [
  { bg: '#e8eeff', color: '#1b4fce' },
  { bg: '#f5f3ff', color: '#6d28d9' },
  { bg: '#eafaf3', color: '#0d7a4e' },
  { bg: '#fffbeb', color: '#b45309' },
  { bg: '#fdf2f1', color: '#c0392b' },
  { bg: '#ecfdf5', color: '#059669' },
  { bg: '#fff1f2', color: '#be123c' },
  { bg: '#f0f9ff', color: '#0369a1' },
];
const getAvatarColor = (name: string | null) => {
  const key = name || 'system';
  if (!avatarColors[key]) {
    const idx = Math.abs([...key].reduce((a, c) => a + c.charCodeAt(0), 0)) % palette.length;
    avatarColors[key] = palette[idx];
  }
  return avatarColors[key];
};

const canRevertLog = (log: AuditLog): boolean => {
  if (log.action !== 'ITEM_UPDATE' || !log.oldValue) return false;
  const hoursDiff = (Date.now() - new Date(log.timestamp).getTime()) / 3_600_000;
  return hoursDiff <= 24;
};

const getResourceIcon = (t: string) => {
  switch (t) {
    case 'ITEM': return faBox;
    case 'CATEGORY': return faFolder;
    case 'USER': return faUser;
    case 'SECURITY': return faShieldAlt;
    case 'SETTINGS': return faCog;
    case 'WORKSPACE': return faGlobe;
    case 'TEAM': return faUsers;
    case 'INVITATION': return faEnvelope;
    case 'COLLECTION': return faLayerGroup;
    case 'COMMENT': return faComment;
    case 'IMAGE': case 'FILE': return faImage;
    case 'AI': return faRobot;
    case 'NOTIFICATION': return faBell;
    case 'BILLING': return faCreditCard;
    case 'EXPORT': return faFileExport;
    default: return faHistory;
  }
};

/* ════════════════════════════════════════════════════════════
   Component
   ════════════════════════════════════════════════════════════ */
const AuditTrail = () => {
  const { t } = useI18n();

  /* state */
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalLogs, setTotalLogs] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [reverting, setReverting] = useState<string | null>(null);

  /* toast */
  const [toasts, setToasts] = useState<{ id: number; msg: string; type: 'success' | 'error' | 'info' | 'warning' }[]>([]);
  const toastId = useRef(0);

  const addToast = useCallback((msg: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(tt => tt.id !== id)), 4000);
  }, []);

  /* confirm dialog */
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; body: string; icon: string; onOk: () => void; okClass: string } | null>(null);

  /* filters */
  const [filterAction, setFilterAction] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRange, setFilterRange] = useState('');

  /* selection */
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /* sort */
  const [sortField, setSortField] = useState<'ts' | 'ev'>('ts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  /* live monitoring */
  const [liveOn, setLiveOn] = useState(false);
  const liveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* ── Fetch ─────────────────────────────────────────────── */
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        limit: perPage,
        offset: (page - 1) * perPage,
      };
      if (filterAction) params.action = filterAction;
      const response = await auditAPI.getLogs(params);
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterAction]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await auditAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
    }
  }, []);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  /* ── Live mode ─────────────────────────────────────────── */
  useEffect(() => {
    if (liveOn) {
      liveRef.current = setInterval(() => { fetchLogs(); fetchStats(); }, 5000);
      return () => { if (liveRef.current) clearInterval(liveRef.current); };
    } else {
      if (liveRef.current) clearInterval(liveRef.current);
    }
  }, [liveOn, fetchLogs, fetchStats]);

  /* ── Local filter / sort (client-side on fetched page) ── */
  const filteredLogs = logs
    .filter(log => {
      if (filterStatus === 'failed' && getStatusClass(log) !== 'failed') return false;
      if (filterStatus === 'success' && getStatusClass(log) !== 'success') return false;
      if (filterRange) {
        const ms: Record<string, number> = { '1h': 3.6e6, '24h': 8.64e7, '7d': 6.048e8, '30d': 2.592e9 };
        if (ms[filterRange] && (Date.now() - new Date(log.timestamp).getTime()) > ms[filterRange]) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const hay = [log.userName, log.action, log.resourceName, log.ipAddress, log.fieldChanged, log.resourceType].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortField === 'ts') {
        return sortDir === 'desc'
          ? new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          : new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      }
      const va = a.action, vb = b.action;
      return sortDir === 'desc' ? vb.localeCompare(va) : va.localeCompare(vb);
    });

  const totalPages = Math.ceil(totalLogs / perPage);

  const mobileCards = filteredLogs.map(log => ({
    id: log.id,
    title: formatAction(log.action),
    actor: log.userName || t('audit.system' as any),
    ago: ago(log.timestamp),
    timestamp: fmtDate(log.timestamp),
    status: getStatusClass(log),
    icon: getActionIcon(log.action),
    badge: getEventClass(log.action),
    eventLabel: formatAction(log.action),
    resource: log.resourceName || log.resourceType || '-',
    source: log.source || 'WEB',
    ip: log.ipAddress || '-',
    description: log.fieldChanged ? `Updated ${log.fieldChanged}` : formatAction(log.action),
  }));

  /* ── Selection ─────────────────────────────────────────── */
  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = (checked: boolean) => {
    if (checked) {
      setSelected(new Set(filteredLogs.map(l => l.id)));
    } else {
      setSelected(new Set());
    }
  };

  /* ── Sort ──────────────────────────────────────────────── */
  const handleSort = (field: 'ts' | 'ev') => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  /* ── Revert ────────────────────────────────────────────── */
  const handleRevert = async (logId: string) => {
    try {
      setReverting(logId);
      const response = await api.post(`/audit/revert/${logId}`);
      if (response.data.success) {
        addToast(response.data.message || 'Changes reverted successfully.', 'warning');
        fetchLogs();
        fetchStats();
      } else {
        addToast(response.data.message || 'Failed to revert change', 'error');
      }
    } catch (error: any) {
      addToast(error.response?.data?.error || 'Failed to revert change', 'error');
    } finally {
      setReverting(null);
    }
  };

  /* ── Export ────────────────────────────────────────────── */
  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await auditAPI.export({ action: filterAction || undefined });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `elvoaq-audit-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast(`${totalLogs} entries exported to CSV.`, 'success');
    } catch {
      addToast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  /* ── Clear filters ─────────────────────────────────────── */
  const clearFilters = () => {
    setSearchQuery('');
    setFilterAction('');
    setFilterStatus('');
    setFilterRange('');
    setPage(1);
  };

  /* ── Pagination builder ────────────────────────────────── */
  const paginationPages = (): (number | '…')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    if (totalPages > 1) pages.push(totalPages);
    return [...new Set(pages)] as (number | '…')[];
  };

  /* ── Stats bar computed values ─────────────────────────── */
  const authCount = stats?.topActions?.filter(a =>
    ['USER_LOGIN', 'USER_LOGOUT', 'USER_2FA_ENABLE', 'USER_REGISTER'].includes(a.action)
  ).reduce((s, a) => s + a.count, 0) ?? 0;

  const changeCount = stats?.topActions?.filter(a =>
    ['ITEM_CREATE', 'ITEM_UPDATE', 'ITEM_DELETE'].includes(a.action)
  ).reduce((s, a) => s + a.count, 0) ?? 0;

  const failedCount = stats?.topActions?.filter(a =>
    a.action.includes('FAILED') || a.action.includes('UNAUTHORIZED')
  ).reduce((s, a) => s + a.count, 0) ?? 0;

  const revertCount = stats?.topActions?.filter(a =>
    a.action.includes('REVERT') || a.action.includes('RESTORE')
  ).reduce((s, a) => s + a.count, 0) ?? 0;

  /* ════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════ */
  return (
    <div className="at-page">
      {/* ── Stats Bar ─────────────────────────────────────── */}
      <div className={`at-stats-bar${isMobile ? ' at-stats-mobile' : ''}`}>
        <div className="at-stat-cell">
          <div className="at-stat-label">{t('audit.totalEvents' as any)}</div>
          <div className="at-stat-number">{(stats?.totalLogs ?? 0).toLocaleString()}</div>
          <div className="at-stat-sub">{stats?.logsToday ?? 0} {t('audit.today' as any)}</div>
        </div>
        <div className="at-stat-cell">
          <div className="at-stat-label">{t('audit.group.auth' as any)}</div>
          <div className="at-stat-number">{authCount}</div>
          <div className="at-stat-sub">{t('audit.statsLogins' as any)}</div>
        </div>
        <div className="at-stat-cell">
          <div className="at-stat-label">{t('audit.group.items' as any)}</div>
          <div className="at-stat-number">{changeCount}</div>
          <div className="at-stat-sub">{t('audit.statsCrud' as any)}</div>
        </div>
        <div className="at-stat-cell">
          <div className="at-stat-label">{t('audit.group.security' as any)}</div>
          <div className="at-stat-number at-num-danger">{failedCount}</div>
          <div className="at-stat-sub">{t('audit.statsAlerts' as any)}</div>
        </div>
        <div className="at-stat-cell">
          <div className="at-stat-label">{t('audit.statsReverts' as any)}</div>
          <div className="at-stat-number at-num-warning">{revertCount}</div>
          <div className="at-stat-sub">{t('audit.statsRollbacks' as any)}</div>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────────────── */}
      {isMobile ? (
        <div className="at-mobile-toolbar">
          <div className="at-search-wrap at-mobile-search">
            <FontAwesomeIcon icon={faSearch} className="at-search-icon" />
            <input
              className="at-search-input"
              type="text"
              placeholder={t('audit.search' as any)}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="at-mobile-toolbar-row">
            <button className="at-btn at-btn-ghost at-btn-sm" onClick={() => setMobileFiltersOpen(v => !v)}>
              <FontAwesomeIcon icon={faCog} /> {mobileFiltersOpen ? 'Hide Filters' : 'Filters'}
            </button>
            <button className="at-btn at-btn-secondary at-btn-sm" onClick={handleExport} disabled={exporting}>
              {exporting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
              {' '}{t('audit.exportCsv' as any)}
            </button>
          </div>

          {mobileFiltersOpen && (
            <div className="at-mobile-filters">
              <select className="at-filter-select" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}>
                <option value="">{t('audit.allActions' as any)}</option>
                <option value="USER_LOGIN">{t('audit.action.login' as any)}</option>
                <option value="USER_LOGOUT">{t('audit.action.logout' as any)}</option>
                <option value="USER_2FA_ENABLE">{t('audit.twoFaVerified' as any)}</option>
                <option value="USER_REGISTER">{t('audit.action.registration' as any)}</option>
                <option value="ITEM_CREATE">{t('audit.action.itemCreated' as any)}</option>
                <option value="ITEM_UPDATE">{t('audit.action.itemUpdated' as any)}</option>
                <option value="ITEM_DELETE">{t('audit.action.itemDeleted' as any)}</option>
                <option value="ITEM_RESTORE">{t('audit.action.itemRestored' as any)}</option>
                <option value="USER_LOGIN_FAILED">{t('audit.action.loginFailed' as any)}</option>
                <option value="BULK_EXPORT">{t('audit.action.bulkExport' as any)}</option>
              </select>

              <select className="at-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">{t('audit.allStatuses' as any)}</option>
                <option value="success">{t('audit.statusSuccess' as any)}</option>
                <option value="failed">{t('audit.statusFailed' as any)}</option>
              </select>

              <select className="at-filter-select" value={filterRange} onChange={e => setFilterRange(e.target.value)}>
                <option value="">{t('audit.allTime' as any)}</option>
                <option value="1h">{t('audit.pastHour' as any)}</option>
                <option value="24h">{t('audit.past24Hours' as any)}</option>
                <option value="7d">{t('audit.past7Days' as any)}</option>
                <option value="30d">{t('audit.past30Days' as any)}</option>
              </select>

              <div className="at-mobile-toolbar-row">
                <button className="at-btn at-btn-ghost at-btn-sm" onClick={clearFilters}>{t('audit.reset' as any)}</button>
                <select className="at-filter-select at-per-page" value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
                  {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="at-toolbar">
          <div className="at-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="at-search-icon" />
            <input
              className="at-search-input"
              type="text"
              placeholder={t('audit.search' as any)}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <select className="at-filter-select" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}>
            <option value="">{t('audit.allActions' as any)}</option>
            <option value="USER_LOGIN">{t('audit.action.login' as any)}</option>
            <option value="USER_LOGOUT">{t('audit.action.logout' as any)}</option>
            <option value="USER_2FA_ENABLE">{t('audit.twoFaVerified' as any)}</option>
            <option value="USER_REGISTER">{t('audit.action.registration' as any)}</option>
            <option value="ITEM_CREATE">{t('audit.action.itemCreated' as any)}</option>
            <option value="ITEM_UPDATE">{t('audit.action.itemUpdated' as any)}</option>
            <option value="ITEM_DELETE">{t('audit.action.itemDeleted' as any)}</option>
            <option value="ITEM_RESTORE">{t('audit.action.itemRestored' as any)}</option>
            <option value="USER_LOGIN_FAILED">{t('audit.action.loginFailed' as any)}</option>
            <option value="BULK_EXPORT">{t('audit.action.bulkExport' as any)}</option>
          </select>

          <select className="at-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">{t('audit.allStatuses' as any)}</option>
            <option value="success">{t('audit.statusSuccess' as any)}</option>
            <option value="failed">{t('audit.statusFailed' as any)}</option>
          </select>

          <select className="at-filter-select" value={filterRange} onChange={e => setFilterRange(e.target.value)}>
            <option value="">{t('audit.allTime' as any)}</option>
            <option value="1h">{t('audit.pastHour' as any)}</option>
            <option value="24h">{t('audit.past24Hours' as any)}</option>
            <option value="7d">{t('audit.past7Days' as any)}</option>
            <option value="30d">{t('audit.past30Days' as any)}</option>
          </select>

          <div className="at-toolbar-sep" />

          {selected.size > 0 && (
            <div className="at-selection-strip">
              <strong>{selected.size}</strong> selected
              <button className="at-btn at-btn-secondary at-btn-xs" onClick={handleExport}>↓ Export</button>
              <button className="at-btn at-btn-ghost at-btn-xs" onClick={() => setSelected(new Set())}>✕ Clear</button>
            </div>
          )}

          <div className="at-toolbar-right">
            <button className="at-btn at-btn-ghost at-btn-sm" onClick={clearFilters}>{t('audit.reset' as any)}</button>
            <select className="at-filter-select at-per-page" value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
              {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <div
              className={`at-live-pill${liveOn ? ' at-live-active' : ''}`}
              onClick={() => setLiveOn(v => !v)}
              role="button"
              tabIndex={0}
            >
              <div className={`at-live-dot${liveOn ? '' : ' at-live-dot-off'}`} />
              {liveOn ? t('audit.live' as any) : t('audit.liveMonitoring' as any)}
            </div>
            <button className="at-btn at-btn-secondary at-btn-sm" onClick={handleExport} disabled={exporting}>
              {exporting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
              {' '}{t('audit.exportCsv' as any)}
            </button>
          </div>
        </div>
      )}

      {/* ── Logs (Table desktop / Cards mobile) ───────────── */}
      {loading ? (
        <div className="at-empty-state">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>{t('audit.loading' as any)}</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="at-empty-state">
          <div className="at-empty-icon">📂</div>
          <div className="at-empty-title">{t('audit.noLogs' as any)}</div>
          <div className="at-empty-sub">{t('audit.emptyState' as any)}</div>
        </div>
      ) : isMobile ? (
        <div className="at-cards">
          {mobileCards.map(card => (
            <article key={card.id} className="at-card" onClick={() => setSelectedLog(logs.find(l => l.id === card.id) || null)}>
              <div className="at-card-head">
                <div className={`at-card-icon at-event-${card.badge}-bg`}>
                  <FontAwesomeIcon icon={card.icon} />
                </div>
                <div className="at-card-meta">
                  <div className="at-card-title">{card.title}</div>
                  <div className="at-card-sub">{card.actor} • {card.ago}</div>
                </div>
              </div>

              <div className="at-card-chips">
                <span className={`at-status-pill at-status-${card.status}`}>
                  <span className="at-status-dot-sm" />
                  {card.status.charAt(0).toUpperCase() + card.status.slice(1)}
                </span>
                <span className={`at-event-badge at-event-${card.badge}`}>
                  <FontAwesomeIcon icon={card.icon} />
                  {card.eventLabel}
                </span>
              </div>

              <div className="at-card-body">
                <div className="at-card-desc">{card.description}</div>
                <div className="at-card-resource">Resource: {card.resource}</div>
                <div className="at-card-row">
                  <span className="at-card-row-label">IP</span>
                  <span className="at-card-row-value at-mono">{card.ip}</span>
                </div>
                <div className="at-card-row">
                  <span className="at-card-row-label">Source</span>
                  <span className="at-card-row-value">{card.source}</span>
                </div>
              </div>

              <div className="at-card-foot">
                <span className="at-card-time">{card.timestamp}</span>
                <button className="at-card-view" onClick={(e) => { e.stopPropagation(); setSelectedLog(logs.find(l => l.id === card.id) || null); }}>
                  <FontAwesomeIcon icon={faEye} /> {t('audit.view' as any)}
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="at-table-container">
          <table className="at-table">
            <thead>
              <tr>
                <th className="at-col-cb">
                  <input type="checkbox" checked={selected.size === filteredLogs.length && filteredLogs.length > 0} onChange={e => selectAll(e.target.checked)} />
                </th>
                <th onClick={() => handleSort('ts')}>
                  <div className="at-th-inner">
                    {t('audit.timestamp' as any)}
                    <FontAwesomeIcon icon={sortField === 'ts' ? (sortDir === 'desc' ? faSortDown : faSortUp) : faSort} className={`at-sort-caret${sortField === 'ts' ? ' at-th-sorted' : ''}`} />
                  </div>
                </th>
                <th>{t('audit.user' as any)}</th>
                <th onClick={() => handleSort('ev')}>
                  <div className="at-th-inner">
                    {t('audit.event' as any)}
                    <FontAwesomeIcon icon={sortField === 'ev' ? (sortDir === 'desc' ? faSortDown : faSortUp) : faSort} className={`at-sort-caret${sortField === 'ev' ? ' at-th-sorted' : ''}`} />
                  </div>
                </th>
                <th>{t('audit.description' as any)}</th>
                <th>{t('audit.status' as any)}</th>
                <th>{t('audit.ipAddress' as any)}</th>
                <th>{t('audit.device' as any)}</th>
                <th style={{ textAlign: 'right', paddingRight: 20 }}>{t('audit.actions' as any)}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => {
                const evClass = getEventClass(log.action);
                const status = getStatusClass(log);
                const ac = getAvatarColor(log.userName);
                const hasRevert = canRevertLog(log);

                return (
                  <tr
                    key={log.id}
                    className={selected.has(log.id) ? 'at-row-selected' : ''}
                    onClick={() => setSelectedLog(log)}
                  >
                    <td className="at-col-cb" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(log.id)} onChange={() => toggleSelect(log.id)} />
                    </td>
                    <td>
                      <div className="at-ts-main">{fmtDate(log.timestamp)}</div>
                      <div className="at-ts-ago">{ago(log.timestamp)}</div>
                    </td>
                    <td>
                      <div className="at-user-cell">
                        <div className="at-user-avatar" style={{ background: ac.bg, color: ac.color }}>{getUserInitials(log.userName)}</div>
                        <div>
                          <div className="at-user-name">{log.userName || t('audit.system' as any)}</div>
                          <div className="at-user-role">{log.userRole || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`at-event-badge at-event-${evClass}`}>
                        <FontAwesomeIcon icon={getActionIcon(log.action)} />
                        {formatAction(log.action)}
                      </span>
                    </td>
                    <td>
                      <div className="at-desc-text" title={log.fieldChanged ? `${log.fieldChanged}: ${log.oldValue} → ${log.newValue}` : formatAction(log.action)}>
                        {log.fieldChanged ? `Updated ${log.fieldChanged}` : formatAction(log.action)}
                      </div>
                      {log.resourceName && <div className="at-desc-resource">{log.resourceName}</div>}
                    </td>
                    <td>
                      <span className={`at-status-pill at-status-${status}`}>
                        <span className="at-status-dot-sm" />
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="at-ip-text">{log.ipAddress || '-'}</div>
                      <div className="at-device-text">{log.source || 'WEB'}</div>
                    </td>
                    <td>
                      <div className="at-ip-text" style={{ fontFamily: 'inherit', fontSize: 12 }}>
                        {log.device ? log.device.split('/')[0]?.trim() : '-'}
                      </div>
                      <div className="at-device-text">
                        {log.device ? (log.device.split('/')[1]?.trim() || '') : ''}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', paddingRight: 20 }} onClick={e => e.stopPropagation()}>
                      <div className="at-row-actions">
                        <button className="at-btn at-btn-ghost at-btn-xs" onClick={() => setSelectedLog(log)}>
                          <FontAwesomeIcon icon={faEye} /> {t('audit.view' as any)}
                        </button>
                        {hasRevert && (
                          <button
                            className="at-btn at-btn-revert at-btn-xs"
                            onClick={() => {
                              setConfirmDialog({
                                title: 'Revert Changes',
                                body: `Revert the changes made to <strong>${log.resourceName || 'this entry'}</strong>? A new revert log entry will be created.`,
                                icon: '↩',
                                onOk: () => handleRevert(log.id),
                                okClass: 'at-btn-revert',
                              });
                            }}
                            disabled={reverting === log.id}
                          >
                            {reverting === log.id ? <FontAwesomeIcon icon={faSpinner} spin /> : '↩'} Revert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ────────────────────────────────────── */}
      {totalPages > 0 && (
        <div className="at-pagination">
          <div className="at-page-info">
            {filteredLogs.length > 0
              ? `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, totalLogs)} of ${totalLogs.toLocaleString()} entries`
              : 'No entries'}
          </div>
          {isMobile ? (
            <div className="at-mobile-pagination">
              <button className="at-pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <span className="at-mobile-page-current">{page} / {Math.max(1, totalPages)}</span>
              <button className="at-pg-btn" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          ) : (
            <>
              <button className="at-pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div className="at-pg-nums">
                {paginationPages().map((p, i) =>
                  p === '…' ? (
                    <span key={`sep-${i}`} className="at-pg-sep">…</span>
                  ) : (
                    <button key={p} className={`at-pg-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p as number)}>
                      {p}
                    </button>
                  )
                )}
              </div>
              <button className="at-pg-btn" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Detail Modal ──────────────────────────────────── */}
      {selectedLog && (
        <div className="at-overlay at-overlay-open" onClick={() => setSelectedLog(null)}>
          <div className="at-detail-modal" onClick={e => e.stopPropagation()}>
            {/* Head */}
            <div className="at-modal-head">
              <div className={`at-modal-event-icon at-event-${getEventClass(selectedLog.action)}-bg`}>
                <FontAwesomeIcon icon={getActionIcon(selectedLog.action)} />
              </div>
              <div className="at-modal-head-info">
                <div className="at-modal-title">{formatAction(selectedLog.action)}</div>
                <div className="at-modal-subtitle">Entry ID: {selectedLog.id} · {fmtDate(selectedLog.timestamp)}</div>
              </div>
              <button className="at-modal-close" onClick={() => setSelectedLog(null)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Body */}
            <div className="at-modal-body">
              {/* Overview */}
              <div className="at-m-section">
                <div className="at-m-section-title">{t('audit.eventInfo' as any)}</div>
                <div className="at-m-grid at-m-grid-3">
                  <div className="at-m-field">
                    <label>Event Type</label>
                    <span className={`at-event-badge at-event-${getEventClass(selectedLog.action)}`}>
                      <FontAwesomeIcon icon={getActionIcon(selectedLog.action)} />
                      {formatAction(selectedLog.action)}
                    </span>
                  </div>
                  <div className="at-m-field">
                    <label>Status</label>
                    <span className={`at-status-pill at-status-${getStatusClass(selectedLog)}`}>
                      <span className="at-status-dot-sm" />
                      {getStatusClass(selectedLog).charAt(0).toUpperCase() + getStatusClass(selectedLog).slice(1)}
                    </span>
                  </div>
                  <div className="at-m-field">
                    <label>{t('audit.timestamp' as any)}</label>
                    <span className="at-m-val">{fmtDate(selectedLog.timestamp)}</span>
                  </div>
                </div>
              </div>

              {/* User & Session */}
              <div className="at-m-section">
                <div className="at-m-section-title">{t('audit.userInfo' as any)}</div>
                <div className="at-m-grid">
                  <div className="at-m-field">
                    <label>{t('audit.user' as any)}</label>
                    <span className="at-m-val">{selectedLog.userName || t('audit.system' as any)}</span>
                  </div>
                  <div className="at-m-field">
                    <label>{t('audit.role' as any)}</label>
                    <span className="at-m-val">{selectedLog.userRole || '-'}</span>
                  </div>
                  <div className="at-m-field">
                    <label>{t('audit.userId' as any)}</label>
                    <span className="at-m-val at-mono">{selectedLog.userId || '-'}</span>
                  </div>
                  <div className="at-m-field">
                    <label>Time Ago</label>
                    <span className="at-m-val at-muted">{ago(selectedLog.timestamp)}</span>
                  </div>
                </div>
              </div>

              {/* Network */}
              <div className="at-m-section">
                <div className="at-m-section-title">{t('audit.techDetails' as any)}</div>
                <div className="at-m-grid">
                  <div className="at-m-field">
                    <label>{t('audit.ipAddress' as any)}</label>
                    <span className="at-m-val at-mono">{selectedLog.ipAddress || '-'}</span>
                  </div>
                  <div className="at-m-field">
                    <label>{t('audit.source' as any)}</label>
                    <span className="at-m-val">{selectedLog.source || 'WEB'}</span>
                  </div>
                  <div className="at-m-field">
                    <label>{t('audit.device' as any)}</label>
                    <span className="at-m-val">{selectedLog.device || '-'}</span>
                  </div>
                  <div className="at-m-field">
                    <label>{t('audit.userAgent' as any)}</label>
                    <span className="at-m-val at-mono" style={{ fontSize: '11.5px', wordBreak: 'break-all' }}>{selectedLog.userAgent || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="at-m-section">
                <div className="at-m-section-title">Description</div>
                <div className="at-m-field">
                  <div className="at-m-val" style={{ fontSize: 14, lineHeight: 1.5 }}>
                    {selectedLog.fieldChanged ? `Updated field "${selectedLog.fieldChanged}"` : formatAction(selectedLog.action)}
                  </div>
                  {selectedLog.resourceName && (
                    <div className="at-m-val at-mono at-muted" style={{ marginTop: 6 }}>
                      <FontAwesomeIcon icon={getResourceIcon(selectedLog.resourceType)} /> Resource: {selectedLog.resourceName}
                    </div>
                  )}
                </div>
              </div>

              {/* Field Changes (diff table) */}
              {selectedLog.fieldChanged && (
                <div className="at-m-section">
                  <div className="at-m-section-title">{t('audit.fieldChange' as any)}</div>
                  <table className="at-diff-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>{t('audit.oldValue' as any)}</th>
                        <th>{t('audit.newValue' as any)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="at-diff-field">{selectedLog.fieldChanged}</td>
                        <td><span className="at-diff-old">− {selectedLog.oldValue || t('audit.empty' as any)}</span></td>
                        <td><span className="at-diff-new">+ {selectedLog.newValue || t('audit.empty' as any)}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="at-m-section">
                  <div className="at-m-section-title">{t('audit.metadata' as any)}</div>
                  <div className="at-meta-list">
                    {Object.entries(selectedLog.metadata).map(([k, v]) => (
                      <div key={k} className="at-meta-row">
                        <span className="at-meta-key">{k}</span>
                        <span className="at-meta-val">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Foot */}
            <div className="at-modal-foot">
              <div className="at-modal-foot-left">
                <button className="at-btn at-btn-secondary at-btn-sm" onClick={() => { navigator.clipboard?.writeText(selectedLog.id); addToast('Entry ID copied.', 'info'); }}>
                  <FontAwesomeIcon icon={faCopy} /> Copy ID
                </button>
                {canRevertLog(selectedLog) && (
                  <button
                    className="at-btn at-btn-revert at-btn-sm"
                    onClick={() => {
                      setConfirmDialog({
                        title: 'Revert Changes',
                        body: `Revert the changes made to <strong>${selectedLog.resourceName || 'this entry'}</strong>?`,
                        icon: '↩',
                        onOk: () => { handleRevert(selectedLog.id); setSelectedLog(null); },
                        okClass: 'at-btn-revert',
                      });
                    }}
                    disabled={reverting === selectedLog.id}
                  >
                    {reverting === selectedLog.id ? <FontAwesomeIcon icon={faSpinner} spin /> : '↩'} {t('audit.revertToPrevious' as any)}
                  </button>
                )}
              </div>
              <button className="at-btn at-btn-primary at-btn-sm" onClick={() => setSelectedLog(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Modal ─────────────────────────────────── */}
      {confirmDialog && (
        <div className="at-overlay at-overlay-open" onClick={() => setConfirmDialog(null)}>
          <div className="at-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="at-confirm-head">
              <div className="at-confirm-icon">{confirmDialog.icon}</div>
              <div className="at-confirm-title">{confirmDialog.title}</div>
              <div className="at-confirm-body" dangerouslySetInnerHTML={{ __html: confirmDialog.body }} />
            </div>
            <div className="at-confirm-foot">
              <button className="at-btn at-btn-secondary at-btn-sm" onClick={() => setConfirmDialog(null)}>Cancel</button>
              <button className={`at-btn ${confirmDialog.okClass} at-btn-sm`} onClick={() => { confirmDialog.onOk(); setConfirmDialog(null); }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toasts ────────────────────────────────────────── */}
      <div className="at-toast-stack">
        {toasts.map(tt => (
          <div key={tt.id} className={`at-toast at-toast-${tt.type}`}>
            <span className="at-toast-icon">
              {tt.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} />}
              {tt.type === 'error' && <FontAwesomeIcon icon={faTimes} />}
              {tt.type === 'info' && <FontAwesomeIcon icon={faEye} />}
              {tt.type === 'warning' && <FontAwesomeIcon icon={faUndo} />}
            </span>
            <span>{tt.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditTrail;
